// EducationPage — Education library view for AI Builder Hub.
//
// Presents a flat, taggable list of training resources — no fixed track
// grouping. What a card renders (inline player, attachment row, link
// preview, or plain text) is derived from whichever content is actually
// attached (uploaded file vs. linked URL), not a separate manually-chosen
// "kind" field.
//
// Design rules (AI_LAB_DESIGN_PRINCIPLES.md):
// - 4px grid, semantic color tokens (--surface, --ink, --green, etc.)
// - Full interactive-state coverage (hover, focus-visible)
// - Light / dark mode via CSS var() tokens
// - Resources cross-posted to the Hub Feed get the .education-card--in-feed
//   highlight style
//
// The component is presentation-only; it fetches from the API on mount and
// filters resources by tag client-side.

import type { FormEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api-client";
import { isSafeHttpUrl } from "@/lib/url";
import { useProfile } from "@/lib/useProfile";
import { Avatar, timeAgo } from "@/features/hub-feed/design-atoms";
import { useAuthors, type AuthorMap } from "@/features/hub-feed/useAuthors";
import "./education-page.css";

// Content-format badge color — mirrors ProjectsPage's project_type badge so
// Learning tiles read like Project tiles. Keyed by the display label, which is
// either the curator-chosen format (Video/Webinar/Article/Recording/Guide) or,
// when none was set, a label derived from the attached content (adds Audio/PDF/
// Link). Both vocabularies share this map.
const EDUCATION_TYPE_COLOR: Record<string, string> = {
  Video: "var(--nds, #5b2d8e)",
  Webinar: "var(--ss, #bd1f6a)",
  Article: "var(--nbs, #1a6fa3)",
  Recording: "#b45309",
  Audio: "var(--ss, #bd1f6a)",
  PDF: "#b45309",
  Link: "var(--nbs, #1a6fa3)",
  Guide: "var(--nfs, #0f7d87)",
};

// Curator-selectable content formats — must match the ContentFormat Literal in
// api/routers/admin_training.py. `value` is what the API stores; `label` is the
// badge/menu text.
export const CONTENT_FORMATS = [
  { value: "video", label: "Video" },
  { value: "webinar", label: "Webinar" },
  { value: "article", label: "Article" },
  { value: "recording", label: "Recording" },
  { value: "guide", label: "Guide" },
] as const;

const CONTENT_FORMAT_LABEL: Record<string, string> = Object.fromEntries(
  CONTENT_FORMATS.map((f) => [f.value, f.label]),
);

// ---------------------------------------------------------------------------
// Types — mirror api/schemas/training.py (NOT hand-edited api-types.ts)
// ---------------------------------------------------------------------------

export interface TrainingResourceItem {
  id: string;
  title: string;
  tags: string[];
  url?: string | null;
  file_url?: string | null;
  file_name?: string | null;
  file_content_type?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
  og_site_name?: string | null;
  added_by_id: string | null;
  description: string | null;
  /** Curator-chosen format (video/webinar/article/recording/guide); null → derive from content. */
  content_format?: string | null;
  /** Learning-series name this resource is grouped under; null → stands alone. */
  series?: string | null;
  /** Position within the series (ascending; nulls sort last). */
  series_order?: number | null;
  posted_to_feed: boolean;
  vote_count: number;
  created_at: string | null;
}

export interface EmptyState {
  headline: string;
  body: string;
}

export interface TrainingListResponse {
  items: TrainingResourceItem[];
  empty_state: EmptyState | null;
  total: number;
}

// ---------------------------------------------------------------------------
// Upload accept list — mirrors api.services.file_storage's content-type
// allow-list (video, audio, PDF only — never text/html or image/svg+xml,
// which would be a same-origin XSS vector if served back through the app).
// ---------------------------------------------------------------------------

export const UPLOAD_ACCEPT =
  "video/mp4,video/webm,audio/mpeg,audio/mp4,audio/wav,audio/webm,application/pdf";

// Azure Container Apps' ingress proxy enforces an undocumented, non-configurable
// request body limit well below this app's own 500 MB setting (core.config.
// settings.max_file_upload_mb) — large uploads can fail there with a dropped
// connection rather than a clean 413, before the request ever reaches the API.
// There's no server-side fix (see deploy/modules/container-app/main.tf: the
// ingress block exposes no body-size knob), so we warn early and point people
// at the URL field (e.g. a SharePoint/Stream/Zoom link) instead for anything
// large — a warning here is much cheaper than a failed upload downstream.
export const LARGE_FILE_WARNING_BYTES = 20 * 1024 * 1024; // 20 MB

export function isLikelyTooLargeForUpload(file: File | null): boolean {
  return file != null && file.size > LARGE_FILE_WARNING_BYTES;
}

// ---------------------------------------------------------------------------
// API fetch
// ---------------------------------------------------------------------------

async function fetchTraining(): Promise<TrainingListResponse> {
  return apiClient<TrainingListResponse>("/api/v1/training");
}

/** Request body for POST /api/v1/admin/training (admin-only). */
interface CreateTrainingPayload {
  title: string;
  tags?: string[];
  url?: string | null;
  description?: string | null;
  content_format?: string | null;
  series?: string | null;
  series_order?: number | null;
  posted_to_feed?: boolean;
}

/** Create a learning resource. Admin-only — the backend returns 403 otherwise. */
async function createTraining(
  data: CreateTrainingPayload,
): Promise<TrainingResourceItem> {
  return apiClient<TrainingResourceItem>("/api/v1/admin/training", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

/** Request body for PATCH /api/v1/admin/training/{id} (admin-only). */
export interface UpdateTrainingPayload {
  title: string;
  tags?: string[];
  url?: string | null;
  description?: string | null;
  content_format?: string | null;
  series?: string | null;
  series_order?: number | null;
  posted_to_feed?: boolean;
}

/** Edit a learning resource's fields. Admin-only — the backend returns 403 otherwise. */
export async function updateTraining(
  resourceId: string,
  data: UpdateTrainingPayload,
): Promise<TrainingResourceItem> {
  return apiClient<TrainingResourceItem>(
    `/api/v1/admin/training/${encodeURIComponent(resourceId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

/** Upload a file (video/audio/PDF) and attach it to a resource. Admin-only. */
export async function uploadTrainingFile(
  resourceId: string,
  file: File,
): Promise<TrainingResourceItem> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient<TrainingResourceItem>(
    `/api/v1/admin/training/${encodeURIComponent(resourceId)}/file`,
    { method: "POST", body: formData },
  );
}

// ---------------------------------------------------------------------------
// Featured badge icon (spark / star — matches design's <Icon.spark>)
// ---------------------------------------------------------------------------

function SparkIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// FileIcon — generic document icon for the non-video/audio attachment row
// ---------------------------------------------------------------------------

function FileIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="13 2 13 9 20 9" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// TrainingCard
// ---------------------------------------------------------------------------

interface TrainingCardProps {
  resource: TrainingResourceItem;
  onOpenDetail: (resourceId: string) => void;
  authors?: AuthorMap;
}

function TrainingCard({ resource, onOpenDetail, authors = {} }: TrainingCardProps) {
  const [previewImgFailed, setPreviewImgFailed] = useState(false);

  const fileUrl = resource.file_url ?? null;
  const contentType = resource.file_content_type ?? "";
  const isVideo = fileUrl != null && contentType.startsWith("video/");
  const isAudio = fileUrl != null && contentType.startsWith("audio/");
  const isOtherFile = fileUrl != null && !isVideo && !isAudio;

  const safeUrl = isSafeHttpUrl(resource.url) ? resource.url : null;
  const hasOgPreview =
    !fileUrl &&
    !!safeUrl &&
    !!(resource.og_title || resource.og_description || resource.og_image_url);

  const addedBy = resource.added_by_id ? authors[resource.added_by_id] : undefined;
  const addedByName = addedBy?.name ?? resource.added_by_id ?? "Unknown";

  // Project-tile-style format badge. Prefer the curator's explicit format
  // (e.g. Webinar vs. a plain Video, or Article vs. a generic Link — a
  // distinction the attached content alone can't reveal); fall back to a label
  // derived from what's attached when no format was set.
  const derivedLabel = isVideo
    ? "Video"
    : isAudio
      ? "Audio"
      : isOtherFile
        ? "PDF"
        : safeUrl
          ? "Link"
          : "Guide";
  const typeLabel = resource.content_format
    ? (CONTENT_FORMAT_LABEL[resource.content_format] ?? derivedLabel)
    : derivedLabel;
  const typeColor = EDUCATION_TYPE_COLOR[typeLabel] ?? "var(--ink-2, #44473f)";

  // The whole card opens the resource's full detail page (comments, votes,
  // reactions) — mirrors ProjectsPage's ProjectCard. Nested interactive
  // elements (player, attachment link, link-preview, the plain-link card)
  // stop propagation so their own click behavior doesn't also navigate away;
  // the plain-link case's URL is still reachable from the detail page itself.
  const handleActivate = useCallback(() => {
    onOpenDetail(resource.id);
  }, [onOpenDetail, resource.id]);

  return (
    <article
      className={
        "education-card" + (resource.posted_to_feed ? " education-card--in-feed" : "")
      }
      data-testid="training-card"
      data-resource-id={resource.id}
      aria-label={resource.title}
      role="button"
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={handleActivate}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      {/* Feed cross-post badge */}
      {resource.posted_to_feed && (
        <span className="education-card__feed-badge" data-testid="feed-badge">
          <SparkIcon /> In Feed
        </span>
      )}

      {/* Title */}
      <h4 className="education-card__title">{resource.title}</h4>

      {/* Owner byline — mirrors ProjectsPage's ProjectCard so Learning tiles
          read like Project tiles (avatar + resolved name). */}
      <div className="education-card__owner" data-testid="training-card-owner">
        <Avatar
          id={resource.added_by_id ?? "anon"}
          initials={addedBy?.initials}
          name={addedBy?.name}
          avatar={addedBy?.avatar}
          size={20}
        />
        <span>{resource.added_by_id ? addedByName : "—"}</span>
      </div>

      {/* Description */}
      {resource.description && (
        <p className="education-card__description">{resource.description}</p>
      )}

      {/* Inline video player — stops propagation so scrubbing/playing doesn't
          also navigate to the detail page. */}
      {isVideo && (
        <video
          controls
          className="education-card__video"
          src={fileUrl as string}
          data-testid="training-video"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Inline audio player */}
      {isAudio && (
        <audio
          controls
          className="education-card__audio"
          src={fileUrl as string}
          data-testid="training-audio"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Non-media attachment (e.g. PDF) */}
      {isOtherFile && (
        <a
          className="education-card__attachment"
          href={fileUrl as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="training-attachment"
          onClick={(e) => e.stopPropagation()}
        >
          <FileIcon />
          <span className="education-card__attachment-name">
            {resource.file_name ?? "Attachment"}
          </span>
          <span className="education-card__attachment-open">Open</span>
        </a>
      )}

      {/* Link preview (OpenGraph unfurl) */}
      {hasOgPreview && (
        <a
          className="education-card__preview"
          href={safeUrl as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="training-link-preview"
          onClick={(e) => e.stopPropagation()}
        >
          {resource.og_image_url && !previewImgFailed && isSafeHttpUrl(resource.og_image_url) && (
            <img
              className="education-card__preview-image"
              src={resource.og_image_url}
              alt=""
              loading="lazy"
              onError={() => setPreviewImgFailed(true)}
            />
          )}
          <span className="education-card__preview-body">
            <span className="education-card__preview-site">
              <LinkIcon /> {resource.og_site_name || new URL(safeUrl as string).hostname}
            </span>
            <span className="education-card__preview-title">
              {resource.og_title || safeUrl}
            </span>
          </span>
        </a>
      )}

      {/* Tags */}
      {resource.tags.length > 0 && (
        <div className="education-card__tags" aria-label="Tags">
          {resource.tags.map((tag) => (
            <span key={tag} className="hub-tag">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer: content-type badge, upvotes, and time — same shape as
          ProjectsPage's ProjectCard footer. The whole card is the click
          target (see onClick above). */}
      <div className="education-card__footer">
        <span
          className="education-card__type-badge"
          style={{ color: typeColor }}
          aria-label={`Type: ${typeLabel}`}
        >
          {typeLabel}
        </span>
        <span
          className="education-card__votes"
          aria-label={`${resource.vote_count} upvotes`}
        >
          ▲ {resource.vote_count}
        </span>
        {resource.created_at && (
          <span className="education-card__time">{timeAgo(resource.created_at)}</span>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// EducationEmptyState — rendered when the API returns no resources at all
// ---------------------------------------------------------------------------

interface EducationEmptyStateProps {
  emptyState: EmptyState | null;
}

function EducationEmptyState({ emptyState }: EducationEmptyStateProps) {
  const headline = emptyState?.headline ?? "No learning resources yet"; // [STUB] fallback
  const body =
    emptyState?.body ??
    "Add a video, upload, or link to help your team level up their AI skills."; // [STUB] fallback

  return (
    <div
      className="education-empty"
      data-testid="education-empty-state"
      role="status"
      aria-live="polite"
    >
      <div className="education-empty__icon" aria-hidden="true">
        {/* Book / graduation icon */}
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      </div>
      <p className="education-empty__headline">{headline}</p>
      <p className="education-empty__body">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddLearningForm — admin-only compose form for a new learning resource
// ---------------------------------------------------------------------------

interface AddLearningFormProps {
  onSuccess: () => void;
  /** Existing series names, surfaced as a datalist so admins reuse a name
   *  exactly (grouping is by exact string match) instead of retyping it. */
  existingSeries?: string[];
}

function AddLearningForm({ onSuccess, existingSeries = [] }: AddLearningFormProps) {
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [contentFormat, setContentFormat] = useState("");
  const [series, setSeries] = useState("");
  const [seriesOrder, setSeriesOrder] = useState("");
  const [postedToFeed, setPostedToFeed] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        const parsedTags = tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        const created = await createTraining({
          title: title.trim(),
          tags: parsedTags,
          url: url.trim() || null,
          description: description.trim() || null,
          content_format: contentFormat || null,
          series: series.trim() || null,
          series_order: seriesOrder.trim() ? Number(seriesOrder) : null,
          posted_to_feed: postedToFeed,
        });
        if (file) {
          await uploadTrainingFile(created.id, file);
        }
        onSuccess();
      } catch (err: unknown) {
        const status =
          err && typeof err === "object" && "status" in err
            ? (err as { status: number }).status
            : 0;
        if (status === 403) {
          setError("Only admins can add learning resources.");
        } else if (status === 415) {
          setError("That file type isn't supported. Accepted: video, audio, or PDF.");
        } else if (status === 413) {
          setError("That file is too large.");
        } else if (status === 422) {
          setError("Please check your input — some fields are invalid.");
        } else {
          setError("Could not add the resource. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      title,
      tags,
      url,
      description,
      contentFormat,
      series,
      seriesOrder,
      postedToFeed,
      file,
      onSuccess,
    ],
  );

  return (
    <div className="hub-compose-wrap">
      {error && (
        <div className="hub-compose-error" role="alert" aria-live="assertive">
          {error}
        </div>
      )}
      <form className="hub-compose-form" onSubmit={handleSubmit} aria-label="Add a learning resource">
        <div className="hub-compose-field">
          <label htmlFor="learn-title" className="hub-compose-label">
            Title <span aria-hidden="true">*</span>
          </label>
          <input
            id="learn-title"
            type="text"
            className="hub-compose-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give the resource a title…"
            required
            maxLength={512}
            disabled={submitting}
            aria-required="true"
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-tags" className="hub-compose-label">
            Tags <span className="hub-compose-label--hint">(optional, comma-separated)</span>
          </label>
          <input
            id="learn-tags"
            type="text"
            className="hub-compose-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. ai, prompting, onboarding"
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-format" className="hub-compose-label">
            Format <span className="hub-compose-label--hint">(optional)</span>
          </label>
          <select
            id="learn-format"
            className="hub-compose-input"
            value={contentFormat}
            onChange={(e) => setContentFormat(e.target.value)}
            disabled={submitting}
          >
            <option value="">Auto (from content)</option>
            {CONTENT_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-url" className="hub-compose-label">
            URL <span className="hub-compose-label--hint">(optional — we'll fetch a preview)</span>
          </label>
          <input
            id="learn-url"
            type="url"
            className="hub-compose-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-file" className="hub-compose-label">
            Upload <span className="hub-compose-label--hint">(optional — video, audio, or PDF)</span>
          </label>
          <input
            id="learn-file"
            type="file"
            className="hub-compose-input"
            accept={UPLOAD_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
          />
          {isLikelyTooLargeForUpload(file) && (
            <p className="hub-compose-warning" data-testid="learn-file-large-warning">
              Large files can fail to upload. For recordings like this, paste a
              link (SharePoint, Stream, Zoom cloud, etc.) in the URL field
              above instead.
            </p>
          )}
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-desc" className="hub-compose-label">
            Description <span className="hub-compose-label--hint">(optional)</span>
          </label>
          <textarea
            id="learn-desc"
            className="hub-compose-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A short summary of what this covers…"
            rows={4}
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="learn-series" className="hub-compose-label">
            Series{" "}
            <span className="hub-compose-label--hint">
              (optional — group multiple sessions into one program)
            </span>
          </label>
          <input
            id="learn-series"
            type="text"
            className="hub-compose-input"
            list="learn-series-options"
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            placeholder="e.g. MCP Series"
            maxLength={256}
            disabled={submitting}
          />
          {existingSeries.length > 0 && (
            <datalist id="learn-series-options">
              {existingSeries.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </div>

        {series.trim() && (
          <div className="hub-compose-field">
            <label htmlFor="learn-series-order" className="hub-compose-label">
              Session number{" "}
              <span className="hub-compose-label--hint">(optional — orders the series)</span>
            </label>
            <input
              id="learn-series-order"
              type="number"
              min={0}
              className="hub-compose-input"
              value={seriesOrder}
              onChange={(e) => setSeriesOrder(e.target.value)}
              placeholder="e.g. 1"
              disabled={submitting}
            />
          </div>
        )}

        <div className="hub-compose-field">
          <label className="hub-compose-label">
            <input
              type="checkbox"
              checked={postedToFeed}
              onChange={(e) => setPostedToFeed(e.target.checked)}
              disabled={submitting}
            />{" "}
            Post to feed
          </label>
        </div>

        <button
          type="submit"
          className="hub-compose-submit"
          disabled={submitting}
          aria-disabled={submitting}
        >
          {submitting ? "Adding…" : "Add resource"}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EducationPage
// ---------------------------------------------------------------------------

export interface EducationPageProps {
  /** Opens the full-page detail view (TrainingDetail) for a resource, mirroring
   *  ProjectsPage's onSelectProject / HubFeed's onOpenPost. */
  onOpenResource: (resourceId: string) => void;
}

export function EducationPage({ onOpenResource }: EducationPageProps) {
  const [data, setData] = useState<TrainingListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [activeFormat, setActiveFormat] = useState<string | null>(null);
  const { isAdmin } = useProfile();
  const authors = useAuthors();

  // Close the compose modal on Escape.
  useEffect(() => {
    if (!composeOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setComposeOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [composeOpen]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchTraining()
      .then((result) => {
        setData(result);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const allTags = useMemo(
    () => Array.from(new Set(data?.items.flatMap((r) => r.tags) ?? [])).sort(),
    [data],
  );

  // Distinct formats actually present, in the canonical CONTENT_FORMATS order,
  // so the filter row only offers formats that exist in the library.
  const allFormats = useMemo(() => {
    const present = new Set(
      (data?.items ?? []).map((r) => r.content_format).filter((f): f is string => !!f),
    );
    return CONTENT_FORMATS.filter((f) => present.has(f.value));
  }, [data]);

  // Existing series names (for the compose datalist) so admins reuse a name
  // exactly rather than accidentally forking a series with a typo.
  const existingSeries = useMemo(
    () =>
      Array.from(
        new Set((data?.items ?? []).map((r) => r.series).filter((s): s is string => !!s)),
      ).sort(),
    [data],
  );

  const visibleItems = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter(
      (r) =>
        (!activeTag || r.tags.includes(activeTag)) &&
        (!activeFormat || r.content_format === activeFormat),
    );
  }, [data, activeTag, activeFormat]);

  // Group the visible items into learning series (by exact series name) plus a
  // list of standalone resources. Within a series, order by series_order asc
  // (nulls last), then created_at, so sessions read in sequence. Series
  // themselves keep the API's newest-first order (insertion order here).
  const { seriesGroups, ungrouped } = useMemo(() => {
    const groups = new Map<string, TrainingResourceItem[]>();
    const solo: TrainingResourceItem[] = [];
    for (const r of visibleItems) {
      if (r.series) {
        const arr = groups.get(r.series) ?? [];
        arr.push(r);
        groups.set(r.series, arr);
      } else {
        solo.push(r);
      }
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => {
        const ao = a.series_order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.series_order ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return (a.created_at ?? "").localeCompare(b.created_at ?? "");
      });
    }
    return { seriesGroups: [...groups.entries()], ungrouped: solo };
  }, [visibleItems]);

  return (
    <div className="education-page" data-testid="education-page">
      {/* Page header */}
      <div className="education-page__head">
        <div>
          <div className="education-page__eyebrow">Learn at your pace</div>
          <h1 className="education-page__title">Education library</h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="hub-newpost"
            data-testid="education-add"
            onClick={() => setComposeOpen(true)}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Learning
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="education-loading" role="status" aria-live="polite">
          Loading resources…
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="education-error" role="alert">
          <strong>Could not load training resources.</strong>
          <p>{error.message}</p>
        </div>
      )}

      {/* Content */}
      {data && !loading && (
        <>
          {data.items.length === 0 ? (
            <EducationEmptyState emptyState={data.empty_state} />
          ) : (
            <>
              {allFormats.length > 0 && (
                <div
                  className="education-tag-filter"
                  role="group"
                  aria-label="Filter by format"
                  data-testid="education-format-filter"
                >
                  {allFormats.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      className={
                        "education-tag-filter__pill" +
                        (activeFormat === f.value ? " education-tag-filter__pill--active" : "")
                      }
                      aria-pressed={activeFormat === f.value}
                      onClick={() =>
                        setActiveFormat((current) => (current === f.value ? null : f.value))
                      }
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
              {allTags.length > 0 && (
                <div
                  className="education-tag-filter"
                  role="group"
                  aria-label="Filter by tag"
                  data-testid="education-tag-filter"
                >
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className={
                        "education-tag-filter__pill" +
                        (activeTag === tag ? " education-tag-filter__pill--active" : "")
                      }
                      aria-pressed={activeTag === tag}
                      onClick={() => setActiveTag((current) => (current === tag ? null : tag))}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Learning series — each renders as a labeled section with its
                  sessions in order, above the standalone resources. */}
              {seriesGroups.map(([name, items]) => (
                <section
                  className="education-series"
                  key={name}
                  data-testid="education-series"
                  aria-label={`${name} series`}
                >
                  <div className="education-series__head">
                    <h3 className="education-series__title">{name}</h3>
                    <span className="education-series__count">
                      {items.length} {items.length === 1 ? "session" : "sessions"}
                    </span>
                  </div>
                  <div className="education-track__grid">
                    {items.map((r) => (
                      <TrainingCard
                        key={r.id}
                        resource={r}
                        onOpenDetail={onOpenResource}
                        authors={authors}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {ungrouped.length > 0 && (
                <div className="education-track__grid" data-testid="education-grid">
                  {ungrouped.map((r) => (
                    <TrainingCard
                      key={r.id}
                      resource={r}
                      onOpenDetail={onOpenResource}
                      authors={authors}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Add Learning modal — admin-only (gated by the button above and by the
          backend, which returns 403 for non-admins). */}
      {composeOpen && (
        <div
          className="hub-modal-overlay"
          data-testid="education-compose-modal"
          onClick={() => setComposeOpen(false)}
        >
          <div
            className="hub-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add a learning resource"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hub-modal__head">
              <h2 className="hub-modal__title">Add Learning</h2>
              <button
                type="button"
                className="hub-modal__close"
                aria-label="Close"
                onClick={() => setComposeOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <AddLearningForm
              existingSeries={existingSeries}
              onSuccess={() => {
                setComposeOpen(false);
                load();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
